import { Modal } from 'components/core-ui/Modal'
import {
  getDailyReportDetails,
  getDailyReportDocumentPreview
} from 'components/waste-management/api'
import { useNotification } from 'hooks'
import { Button, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { Box, CircularProgress, Grid, Typography, useMediaQuery } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Document, Page, pdfjs } from 'react-pdf'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { DailyReportReportTableItem } from 'components/waste-management/interfaces'

pdfjs.GlobalWorkerOptions.workerSrc = 'static/pdfWorker/pdf.worker.min.js'

type DetailsModalProps = {
  isModalOpen: boolean
  closeModal: () => void
  detailsRow?: DailyReportReportTableItem
}

const DetailsModal = (props: DetailsModalProps) => {
  const { isModalOpen, closeModal, detailsRow } = props

  const { showNotification } = useNotification()

  const { data: dailyReport } = getDailyReportDetails(showNotification, detailsRow?.id, isModalOpen)

  const { data: documentData, isFetching: isDocumentLoading } = getDailyReportDocumentPreview(
    showNotification,
    detailsRow?.id,
    isModalOpen,
    !!detailsRow?.documentNo
  )

  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)

  const componentRef = useRef<HTMLDivElement | null>(null)

  const printReport = useReactToPrint({
    content: () => componentRef.current
  })

  const isXs = useMediaQuery('(max-width:624px)')

  useEffect(() => {
    setScale(isXs ? 0.5 : 1.0)
  }, [isXs])

  useEffect(() => {
    setPageNumber(1)
    setNumPages(null)
  }, [detailsRow?.id])

  const previewUrl = useMemo(() => {
    if (!documentData?.blob) return null
    return URL.createObjectURL(documentData.blob)
  }, [documentData?.blob])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
  }

  const onDocumentLoadError = (error: { message?: string }) => {
    if (error?.message === 'Worker was destroyed') return

    showNotification({
      message: `A aparut o eroare la afisarea fisierului. ${error.message ?? ''}`,
      type: 'error'
    })
  }

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1))
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages ?? prev))

  const isImage = documentData?.contentType?.startsWith('image/')
  const isPdf = documentData?.contentType === 'application/pdf'

  const renderPreview = () => {
    if (isDocumentLoading) {
      return (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress color="primary" />
        </Box>
      )
    }

    if (!previewUrl) {
      return (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Typography variant="body1">
            Nu exista nici un document atașat la acest raport.
          </Typography>
        </Box>
      )
    }

    if (isImage) {
      return (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 16
          }}
        >
          <img
            src={previewUrl}
            alt="Document preview"
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
          <Box style={{ display: 'none' }}>
            <Box ref={componentRef}>
              <img src={previewUrl} alt="Document preview" style={{ maxWidth: '100%' }} />
            </Box>
          </Box>
        </Box>
      )
    }

    if (isPdf) {
      return (
        <Box style={{ textAlign: 'center', padding: 16 }}>
          <Box>
            <IconButton
              onClick={goToPrevPage}
              startIcon={<ChevronLeft />}
              variant="text"
              disabled={pageNumber <= 1}
            />
            <span>
              Pagina {pageNumber} din {numPages ?? '-'}
            </span>
            <IconButton
              onClick={goToNextPage}
              startIcon={<ChevronRight />}
              variant="text"
              disabled={!numPages || pageNumber >= numPages}
            />
          </Box>
          <Box>
            <Document
              file={previewUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<CircularProgress color="primary" style={{ margin: '40px auto' }} />}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                loading={<CircularProgress color="primary" style={{ margin: '40px auto' }} />}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          </Box>
          <Box style={{ display: 'none' }}>
            <Box ref={componentRef}>
              <Document
                file={previewUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<CircularProgress color="primary" style={{ margin: '40px auto' }} />}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    scale={1.5}
                    loading={<CircularProgress color="primary" style={{ margin: '40px auto' }} />}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                ))}
              </Document>
            </Box>
          </Box>
        </Box>
      )
    }

    return null
  }

  const canPrint = !isDocumentLoading && !!previewUrl && (isImage || isPdf)

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={`Detalii raport ${dailyReport?.reportNo ?? ''}`}
      modalWidth="50%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} item>
          {renderPreview()}
        </Grid>
        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={closeModal} />
          <Button
            variant="contained"
            label="Printeaza"
            onClick={printReport}
            disabled={!canPrint}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default DetailsModal
